import React from 'react';
import clsx from 'clsx';
import Icon from '../Icon';

const formatUSDC = (val) => {
  const n = parseFloat(val || 0);
  return n >= 1000 ? `$${(n / 1000).toFixed(1)}K` : `$${n.toFixed(2)}`;
};

const TYPE_LABELS = {
  treasury:        'Treasury — Gnosis Safe',
  paymaster:       'Paymaster',
  rounds_pool:     'Rounds Pool',
  harambee_pool:   'Harambee Pool',
  user_smart_wallet: 'User wallet',
};

const TYPE_ACCENT = {
  treasury:        'var(--teal)',
  paymaster:       'var(--info)',
  rounds_pool:     'var(--warn)',
  harambee_pool:   'var(--ok)',
  user_smart_wallet: 'var(--tx2)',
};

const WalletCard = ({ wallet }) => {
  const accent = TYPE_ACCENT[wallet.type] || 'var(--teal)';
  const label = wallet.label || TYPE_LABELS[wallet.type] || wallet.type;
  const isLow = wallet.low_balance_threshold &&
    parseFloat(wallet.usdc_balance) < parseFloat(wallet.low_balance_threshold);

  return (
    <div
      className="card"
      style={{
        padding: 'var(--s4)',
        borderColor: isLow ? 'var(--err)' : 'var(--bd)',
      }}
    >
      <div className="row row-gap-3 mb-3" style={{ alignItems: 'flex-start' }}>
        <div
          style={{
            width: 36, height: 36,
            borderRadius: 'var(--r-md)',
            background: 'var(--teal-faint)',
            color: accent,
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
          }}
        >
          <Icon name="wallet" size={18} />
        </div>
        <div className="flex-1" style={{ minWidth: 0 }}>
          <div className="font-semibold" style={{ color: 'var(--tx1)', fontSize: 13 }}>{label}</div>
          <div className="text-xs" style={{ color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginTop: 2 }}>
            {wallet.network}
          </div>
        </div>
        {isLow && <span className="badge badge-danger">Low</span>}
      </div>

      <div className="responsive-2col-tight">
        <div>
          <div className="text-xs" style={{ color: 'var(--tx3)' }}>USDC balance</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--teal)', letterSpacing: '-0.01em' }}>
            {formatUSDC(wallet.usdc_balance)}
          </div>
        </div>
        <div>
          <div className="text-xs" style={{ color: 'var(--tx3)' }}>Native balance</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--tx1)', letterSpacing: '-0.01em' }}>
            {parseFloat(wallet.native_balance || 0).toFixed(4)}
          </div>
        </div>
      </div>

      {wallet.type === 'treasury' && wallet.safe_threshold && (
        <div className="text-xs" style={{ marginTop: 'var(--s3)', paddingTop: 'var(--s2)', borderTop: '1px solid var(--bd)', color: 'var(--tx2)' }}>
          Signers: {wallet.safe_signers?.length || 0} · Threshold: {wallet.safe_threshold}
        </div>
      )}

      {(wallet.type === 'rounds_pool' || wallet.type === 'harambee_pool') && (
        <div style={{ marginTop: 'var(--s3)', paddingTop: 'var(--s2)', borderTop: '1px solid var(--bd)' }}>
          <div className="row text-xs" style={{ justifyContent: 'space-between', color: 'var(--tx2)' }}>
            <span>TVL: <strong style={{ color: 'var(--tx1)' }}>{formatUSDC(wallet.total_value_locked)}</strong></span>
            <span>{wallet.active_participants || 0} participants</span>
          </div>
          {wallet.next_distribution_at && (
            <div className="text-xs" style={{ marginTop: 4, color: 'var(--warn)' }}>
              Next distribution: {new Date(wallet.next_distribution_at).toLocaleDateString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const WalletStatus = ({ wallets = [] }) => {
  const order = ['treasury', 'paymaster', 'rounds_pool', 'harambee_pool', 'user_smart_wallet'];
  const sorted = [...wallets].sort(
    (a, b) => (order.indexOf(a.type) === -1 ? 99 : order.indexOf(a.type)) - (order.indexOf(b.type) === -1 ? 99 : order.indexOf(b.type))
  );

  return (
    <div className="card">
      <div className="row row-gap-3 mb-3">
        <Icon name="wallet" size={18} style={{ color: 'var(--teal)' }} />
        <div className="card-title" style={{ margin: 0 }}>Wallet status</div>
      </div>
      {sorted.length === 0 ? (
        <div className="empty" style={{ padding: 'var(--s8) var(--s4)' }}>
          <div className="empty-icon"><Icon name="wallet" size={22} /></div>
          <h3 style={{ fontSize: 14 }}>No wallets connected</h3>
          <p>Treasury, paymaster, and pool balances will appear here once wallets are configured.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--s3)' }}>
          {sorted.map((wallet, i) => (
            <WalletCard key={wallet.id || i} wallet={wallet} />
          ))}
        </div>
      )}
    </div>
  );
};

export default WalletStatus;
